import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDown, Calculator as CalcIcon } from "lucide-react";
import { Sticky } from "./NoteBlocks";

const LINES = ["Spend ₹1.", "Earn ₹4.", "Samjho kaise."];
const ease = [0.77, 0, 0.175, 1];

const MaskLine = ({ text, i }) => (
  <span className="block overflow-hidden pb-2">
    <motion.span className="block" initial={{ y: "110%", rotate: 3 }} animate={{ y: "0%", rotate: 0 }} transition={{ duration: 1.1, delay: 0.2 + i * 0.16, ease }}>
      {i === 1 ? <span className="relative inline-block">{text}<Underline /></span> : text}
    </motion.span>
  </span>
);

const Underline = () => (
  <svg className="absolute left-0 -bottom-2 w-full h-5" viewBox="0 0 300 20" preserveAspectRatio="none" aria-hidden>
    <motion.path d="M4,14 C80,4 160,18 296,6" stroke="#E4572E" strokeWidth="6" fill="none" strokeLinecap="round"
      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.9, delay: 1.2, ease: "easeInOut" }} />
  </svg>
);

const Board = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0), my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 80, damping: 15 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 80, damping: 15 });
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 600], [0, -80]);
  const y2 = useTransform(scrollY, [0, 600], [0, 60]);
  const move = (e) => {
    const b = ref.current.getBoundingClientRect();
    mx.set((e.clientX - b.left) / b.width - 0.5); my.set((e.clientY - b.top) / b.height - 0.5);
  };
  return (
    <div ref={ref} onMouseMove={move} onMouseLeave={() => { mx.set(0); my.set(0); }} className="relative h-[420px] sm:h-[520px] [perspective:1200px]" data-testid="hero-board">
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="absolute inset-0 [transform-style:preserve-3d]">
        <motion.div initial={{ opacity: 0, scale: 0.9, rotate: -8 }} animate={{ opacity: 1, scale: 1, rotate: -4 }} transition={{ delay: 0.5, duration: 1, ease }}
          className="polaroid absolute left-[6%] top-[4%] w-[62%] sm:w-[58%]" style={{ translateZ: 20 }}>
          <div className="aspect-[4/5] overflow-hidden"><img src="/hands.jpg" alt="Writing notes on sticky paper" className="w-full h-full object-cover" /></div>
          <p className="font-hand text-lg text-center pt-2">notes &gt; theory</p>
        </motion.div>
        <motion.div style={{ y: y1, translateZ: 70 }} initial={{ opacity: 0, y: -60, rotate: 10 }} animate={{ opacity: 1, y: 0, rotate: 5 }} transition={{ delay: 1.4, type: "spring", stiffness: 120, damping: 12 }}
          className="absolute right-[2%] top-[8%] w-[46%] sm:w-[40%]">
          <Sticky color="yellow" rotate={0} testId="hero-sticky-roas">
            <p className="font-hand text-[15px] text-[#4A4A55]">ROAS =</p>
            <p className="font-display text-3xl sm:text-4xl leading-none">₹50,000 ÷ ₹10,000</p>
            <p className="font-display text-5xl text-[#E4572E] mt-1">= 5x</p>
          </Sticky>
        </motion.div>
        <motion.div style={{ y: y2, translateZ: 50 }} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.7, duration: 0.8, ease }}
          className="absolute right-[6%] bottom-[6%] w-[48%] sm:w-[42%]">
          <Sticky color="blue" rotate={-3}>
            <p className="font-hand text-xl leading-snug">5x ROAS ≠ 5x profit.</p>
            <p className="text-[13px] mt-1">Product cost, shipping, RTO — sab minus karo.</p>
          </Sticky>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} style={{ translateZ: 40 }}
          className="absolute left-[2%] bottom-[2%] w-[40%] sm:w-[34%]">
          <Sticky color="pink" rotate={3}><p className="font-hand text-lg">10 modules · 0 fluff</p></Sticky>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Hero = () => (
  <section className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-28 sm:pt-32 pb-16 grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-6 items-center" data-testid="hero-section">
    <div>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="eyebrow">
        Performance Marketing · handwritten notes · by Himanshu Soni
      </motion.p>
      <h1 className="font-display text-[19vw] sm:text-[8.5rem] lg:text-[9.5rem] leading-[0.82] text-[#1F2A44] mt-5" data-testid="hero-title">
        {LINES.map((l, i) => <MaskLine key={l} text={l} i={i} />)}
      </h1>
      <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.7 }} className="text-base sm:text-lg text-[#4A4A55] max-w-xl mt-8 leading-relaxed">
        Digital marketing teaches you to use the platforms. Performance marketing teaches you to <span className="hl">turn ad spend into revenue</span> — and prove it with numbers. 10 modules, written like a friend's notebook.
      </motion.p>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.7 }} className="flex flex-wrap gap-3 mt-9">
        <Link to="/#modules" className="pill-btn" data-testid="hero-open-notes-btn">Open the notes <ArrowDown size={18} /></Link>
        <Link to="/#calculator" className="pill-btn ghost" data-testid="hero-calculator-btn"><CalcIcon size={18} /> ROAS calculator</Link>
      </motion.div>
    </div>
    <Board />
  </section>
);
